<template>
  <form v-on:submit.prevent="onCreate" class="task-creation">
    <input-text-length-limitation
      title="Заголовок"
      v-bind:max-length="200"
      v-bind:violations="getViolationsBySource('task.title')"
      v-model="title"
    />
    <autocomplete-input
      class="task-edition__input"
      clearable
      label="Роль"
      v-model="roleSlug"
      item-text="title"
      item-value="slug"
      required="required"
      v-on:input="onSelectRoleSlug"
      v-bind:items="rolesOptions"
      v-bind:violations="getViolationsBySource('task.role')"
    />
    <autocomplete-input
      class="task-creation__input"
      clearable
      label="Исполнитель"
      v-model="performerId"
      item-text="title"
      item-value="value"
      v-bind:items="performers"
      v-bind:violations="getViolationsBySource('task.performer')"
    />
    <autocomplete-input
      attach
      class="task-creation__input multiline"
      hide-details
      item-text="title"
      item-value="value"
      label="ФИО пациента"
      v-bind:clearable="true"
      v-bind:violations="getViolationsBySource('task.patient')"
      v-bind:items="patients"
      v-bind:search-input.sync="patientSearchQuery"
      v-bind:value="selectedPatientId"
      v-on:search-input="onUpdateSearch"
      v-on:blur="onBlurInputPatient"
      v-on:input="onInputPatient"
    />
    <div class="task-creation__fields-wrapper">
      <div class="task-creation__data">
        <div class="task-creation__data__label">Дедлайн</div>
        <date-picker-dialog
          v-bind:allowed-dates="allowedDates"
          v-bind:value="deadLineDate"
          v-on:input="setNewDate"
        >
          <template v-slot:trigger="{ listenerObject }">
            <button
              title="Сменить дату"
              v-if="deadLineDate"
              type="button"
              v-on="listenerObject"
            >
              {{ deadLineTitle }}
            </button>
          </template>
        </date-picker-dialog>
      </div>
      <div class="task-creation__status">
        <the-select
          title="Статус"
          v-model="status"
          v-bind:options="$options.statuses"
          v-bind:violations="getViolationsBySource('task.status')"
        />
      </div>
    </div>
    <textarea-length-limitation
      placeholder="Подробное описание"
      title="Подробное описание"
      v-model="description"
      v-bind:max-length="300"
      rows="5"
      v-bind:violations="getViolationsBySource('task.description')"
    />
    <div class="task-creation__control-buttons">
      <button
        class="task-creation__cancel"
        type="button"
        v-on:click="() => $emit('cancel')"
      >Отмена</button>
      <button
        class="task-creation__submit"
        type="submit"
      >Сохранить</button>
    </div>
  </form>
</template>

<script>

import DatePickerDialog from '@/components/DatePickerDialog.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TheSelect from '@/components/TheSelect.vue';
import { TaskRequest, statuses } from '@/storage/gateways/TaskGateway';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import debounce from 'lodash.debounce';
import InputTextLengthLimitation from '@/components/InputTextLengthLimitation.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';

export default {
  name: 'FormTaskCreation',
  components: {
    AutocompleteInput,
    InputTextLengthLimitation,
    TheSelect,
    TextareaLengthLimitation,
    DatePickerDialog,
  },
  computed: {
    /**
     * @description Заголовок крайнего срока
     * @return {string}
     */
    deadLineTitle() {
      const options = {
        day: 'numeric',
        month: 'long',
      };
      const weekday = this.deadLineDate.toLocaleDateString('ru-RU', { weekday: 'short' });
      return `${this.deadLineDate.toLocaleDateString('ru-RU', options)}, ${weekday.toUpperCase()}`;
    },
    /**
     * @description Имеет выбор пациента.
     */
    hasPatientSelection() {
      return this.fixedPatientId === null;
    },
    /**
     * @description Состояние не соответствует начальному
     * @return {boolean}
     */
    taskIsChanged() {
      return !(this.deadLine === getISODateStringByDate(this.currentDate)
      && this.description === ''
      && this.performerId === 0
      && this.status === 'new'
      && this.title === ''
      && this.patientId === this.fixedPatientId);
    },
    /**
     * @description Идентификатор выбранного пациента.
     * @returns {number|null}
     */
    selectedPatientId() {
      if (this.patientId === null) {
        return null;
      }
      return +this.patientId;
    },
  },
  data() {
    let currentDate = new Date();
    const deadLine = getISODateStringByDate(currentDate);
    currentDate = new Date(deadLine);
    return {
      /**
       * @description Слаг роли
       * @type {string}
       */
      roleSlug: '',
      /**
       * @description Запрос на поиск пациента.
       * @type {string}
       */
      patientSearchQuery: '',
      /**
       * @description Исполнители
       * @type Array
       */
      performers: this.initialPerformers,
      currentDate, // Состояние, необходимое для определения доступности даты
      deadLine,
      deadLineDate: new Date(deadLine),
      description: '',
      performerId: null,
      patientId: this.fixedPatientId,
      status: 'new',
      title: '',
    };
  },
  methods: {
    /**
     * @description Обработать событие ввода поиска по пациентам.
     * @param {string} query
     * @returns {void}
     */
    onUpdateSearch(query) {
      this.$emit('search-input', query);
    },
    /**
     * @description  Обработать выбор слага роли
     * @param {string} role
     * @returns {void}
     */
    onSelectRoleSlug(role) {
      this.performers = this.initialPerformers.filter((item) => item.role_slug === role);
    },
    /**
     * @description Обработать изменение пациента.
     * @param {?number} value
     * @returns {void}
     */
    onInputPatient(value) {
      this.patientId = value ? value.toString() : null;
      if (value && this.isPatientFieldInvalid) {
        this.isPatientFieldInvalid = false;
      }
    },
    /**
     * @description Обработать потерю фокуса с поля выбора пациента.
     * @returns {void}
     */
    onBlurInputPatient() {
      this.isPatientFieldInvalid = !this.patientId && this.patientSearchQuery;
    },
    /**
     * @description Функция описывающая условие доступности выбора дат в datepicker
     * @param {String} candidate Строка с датой в формате Y-m-d
     * @return {boolean}
     */
    allowedDates(candidate) {
      return (new Date(candidate)) >= this.currentDate;
    },
    /**
     * @description Инициализировать событие поднятие статуса
     * @param val
     */
    dispatchTaskIsChanged(val) {
      this.$emit('task-is-changed', val);
    },
    /**
     * @description Отобразить причины отказа в исполнении запроса
     * @param source
     * @return {*}
     */
    getViolationsBySource(source) {
      return this.violations?.[source] || [];
    },
    /**
     * Событие создания
     */
    onCreate() {
      const request = new TaskRequest(
        this.title,
        this.performerId ?? null,
        this.deadLine,
        this.status,
        this.description,
        this.patientId,
        this.roleSlug,
      );
      this.$emit('create-task', request);
    },
    /**
     * @description Установить новую дату
     * @param {Date} date
     */
    setNewDate(date) {
      this.deadLine = getISODateStringByDate(date);
      this.deadLineDate = date;
    },
  },
  watch: {
    taskIsChanged: {
      handler: debounce(function _(val) {
        this.dispatchTaskIsChanged(val);
      }, 300),
      deep: true,
    },
  },
  props: {
    /**
     * @description Опции ролей
     * @type Array
     */
    rolesOptions: { type: Array, default: () => [], required: true },
    /**
     * @description Идентификатор выбранного пациента.
     */
    fixedPatientId: {
      type: String,
      default: () => null,
    },
    patients: { type: Array, default: () => [] },
    /**
     * @description Начальные исполнители
     * @type Array
     */
    initialPerformers: { type: Array, default: () => [] },
    violations: { type: Object, default: () => {} },
  },
  statuses,
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.task-creation {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
  &__fields-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 10px;
  }
  &__input {
    ::v-deep .v-autocomplete {
      margin: 0;
    }
  }
  &__data {
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90A0B7;
      margin: 0 0 3px 0;
    }
    button {
      padding: 7px 50px 7px 10px;
      width: 100%;
      font-size: 16px;
      text-align: left;
      border: 1px solid #C2CFE0;
      border-radius: 4px;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: right 10px center;
    }
  }
  &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
  }
  &__cancel {
    @include buttons.btn-transparent(10rem, 3rem, 1rem);
  }
  &__control-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 330px;
    width: 100%;
    margin: 30px 0 0 auto;
  }
  &__button {
    @include buttons.btn-blue(215px, 56px, 20px);
    margin: 100px 0 0 auto;
  }
}

@media screen and (max-width: 480px) {
  .task-creation {
    &__fields-wrapper {
      grid-template-columns: 1fr;
    }
  }
}

@media screen and (max-width: 380px) {
  .task-creation {
    &__control-buttons {
      grid-template-columns: 1fr;
    }
    &__cancel,
    &__submit {
      width: 100%;
    }
  }
}
</style>
